import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import {
  AlertCircle,
  ArrowDownToLine,
  Bot,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronsUpDown,
  Edit3,
  ExternalLink,
  HelpCircle,
  Plug,
  Plus,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Sparkles,
  Square,
  Zap,
} from "lucide-react";

/* ─── Domain types (UNCHANGED — backend contract) ─────────────────── */

interface GscRow {
  keys: string[];
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

interface SearchAnalyticsPayload {
  rows: GscRow[];
  hasMore?: boolean;
  siteUrl?: string;
  startDate: string;
  endDate: string;
  dimensions: string[];
}

interface GscStatus {
  configured: boolean;
  ready: boolean;
  redirectUri: string | null;
  defaultSite: string | null;
}

/** Distinguish actionable error categories so the UI can offer the right
 *  CTA. The server-side error string is the only signal we have, so we
 *  match a small whitelist of substrings produced by Google's OAuth
 *  client and our own API. */
type GscErrorKind = "needs-reauth" | "needs-config" | "transient" | "other";

function classifyGscError(rawErr: string | null, status: GscStatus | null): GscErrorKind {
  if (!rawErr) return "other";
  const m = rawErr.toLowerCase();
  if (m.includes("invalid_grant")
    || m.includes("token has been expired or revoked")
    || m.includes("token expired")
    || m.includes("invalid grant")) {
    return "needs-reauth";
  }
  if (m.includes("missing gsc_client")
    || m.includes("missing gsc_refresh_token")
    || m.includes("not connected")
    || (status && !status.ready)) {
    return status && !status.configured ? "needs-config" : "needs-reauth";
  }
  if (m.includes("timed out") || m.includes("timeout") || m.includes("503") || m.includes("502")) {
    return "transient";
  }
  return "other";
}

/* ─── Date / fetch / aggregation helpers (UNCHANGED) ──────────────── */

function formatYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Last N calendar days ending yesterday (inclusive), for GSC lag friendliness. */
function rollingRangeDays(n: number): { startDate: string; endDate: string } {
  const end = new Date();
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(start.getDate() - (n - 1));
  return { startDate: formatYMD(start), endDate: formatYMD(end) };
}

async function postSearchAnalytics(
  body: Record<string, unknown>
): Promise<SearchAnalyticsPayload> {
  const res = await fetch("/api/gsc/search-analytics", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }
  if (!res.ok) {
    const err =
      parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : text || res.statusText;
    throw new Error(err);
  }
  return parsed as SearchAnalyticsPayload;
}

function aggregateFromDaily(rows: GscRow[]) {
  let clicks = 0;
  let impressions = 0;
  let posWeighted = 0;
  for (const r of rows) {
    clicks += r.clicks;
    impressions += r.impressions;
    posWeighted += r.position * r.impressions;
  }
  const ctr = impressions > 0 ? clicks / impressions : 0;
  const position = impressions > 0 ? posWeighted / impressions : 0;
  return { clicks, impressions, ctr, position };
}

function nf(n: number) {
  return new Intl.NumberFormat("en-US").format(Math.round(n));
}

/* ─── GSC-style filter control bar config ─────────────────────────── */
/**
 * Each preset carries the concrete rolling-window length it should map
 * to. Clicking a preset drives BOTH the visual highlight AND the data
 * fetch — load() reads `days` off the active preset, so the chart /
 * scorecards / tables genuinely repopulate against the new window.
 *
 *   - "details" is a placeholder for a future custom-range builder and
 *     currently falls through to the 28-day window with a toast.
 */
const DATE_PRESETS = [
  { id: "24h", label: "24 hours", days: 1 },
  { id: "7d", label: "7 days", days: 7 },
  { id: "28d", label: "28 days", days: 28 },
  { id: "3m", label: "3 months", days: 90 },
  { id: "details", label: "Details", days: 28 },
] as const;
type DatePresetId = (typeof DATE_PRESETS)[number]["id"];

/** GSC `type` parameter for searchanalytics.query — drives Search type
 *  dropdown in the filter bar. */
type SearchType = "web" | "image" | "video" | "news" | "discover" | "googleNews";
const SEARCH_TYPES: { id: SearchType; label: string }[] = [
  { id: "web", label: "Web" },
  { id: "image", label: "Image" },
  { id: "video", label: "Video" },
  { id: "news", label: "News" },
  { id: "discover", label: "Discover" },
  { id: "googleNews", label: "Google News" },
];

/* ─── Metric metadata (single source of truth) ─────────────────────
 *
 * Every metric the dashboard exposes — Clicks / Impressions / CTR /
 * Position — funnels its colour, label and value-formatter through
 * here. Scorecards, the trend chart, the chart legend, the chart
 * tooltip and the dynamic title all read from the SAME record, so
 * picking a new accent or value format is a one-line change.
 *
 *   - `color` is also the Tailwind arbitrary-value class used by the
 *     scorecard (the JIT preserves these literal hex strings in the
 *     production build).
 *   - `inverted: true` is set only on Position — lower is better, so
 *     the chart inverts that line's Y mapping (improvement reads as
 *     "trend up" visually, matching GSC's own behaviour).
 */
type MetricKey = "clicks" | "impressions" | "ctr" | "position";

interface MetricMeta {
  label: string;
  color: string;
  format: (v: number) => string;
  inverted?: boolean;
}

const METRIC_META: Record<MetricKey, MetricMeta> = {
  clicks: {
    label: "Clicks",
    color: "#4285F4",
    format: (v) => new Intl.NumberFormat("en-US").format(Math.round(v)),
  },
  impressions: {
    label: "Impressions",
    color: "#8E24AA",
    format: (v) => new Intl.NumberFormat("en-US").format(Math.round(v)),
  },
  ctr: {
    label: "CTR",
    color: "#0F9D58",
    format: (v) => `${(v * 100).toFixed(2)}%`,
  },
  position: {
    label: "Position",
    color: "#E67C73",
    format: (v) => v.toFixed(1),
    inverted: true,
  },
};

const METRIC_ORDER: MetricKey[] = ["clicks", "impressions", "ctr", "position"];

/** "just now / N mins ago / N hours ago / N days ago" — used for the
 *  "Last updated" label in the filter control bar. */
function formatRelativeUpdate(date: Date | null): string {
  if (!date) return "—";
  const diffMs = Date.now() - date.getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} mins ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hours ago`;
  const d = Math.floor(hr / 24);
  return `${d} days ago`;
}

/* ─── Page ─────────────────────────────────────────────────────────── */

export default function GscOverviewPage() {
  const [status, setStatus] = useState<GscStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [siteUrl, setSiteUrl] = useState<string | null>(null);
  const [range, setRange] = useState(() => rollingRangeDays(28));
  const [daily, setDaily] = useState<
    {
      label: string;
      clicks: number;
      impressions: number;
      ctr: number;
      position: number;
    }[]
  >([]);
  /* Scorecard toggle state — drives both the card's active/inactive skin
   *  AND which lines the trend chart draws. Default mirrors GSC's own
   *  initial selection (Clicks + Impressions). */
  const [activeMetrics, setActiveMetrics] = useState<Record<MetricKey, boolean>>({
    clicks: true,
    impressions: true,
    ctr: false,
    position: false,
  });
  const toggleMetric = useCallback((key: MetricKey) => {
    setActiveMetrics((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const [summary, setSummary] = useState<{
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  } | null>(null);
  const [topQueries, setTopQueries] = useState<GscRow[]>([]);
  const [topPages, setTopPages] = useState<GscRow[]>([]);
  /* Filter-bar state — every change drives a real refetch through load(). */
  const [datePreset, setDatePreset] = useState<DatePresetId>("28d");
  const [searchType, setSearchType] = useState<SearchType>("web");
  /* Search-type dropdown open/close + click-outside ref. */
  const [searchTypeOpen, setSearchTypeOpen] = useState(false);
  const searchTypeRef = useRef<HTMLDivElement>(null);
  /* Lightweight toast for "feature reserved" CTAs (+ New, Sliders, Daily). */
  const [toast, setToast] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  /* Tick once a minute so the "X mins ago" label stays fresh without
     coupling to data refetches. */
  const [nowTick, setNowTick] = useState(0);

  /**
   * load() — pulls everything the dashboard needs in one Promise.all.
   *
   * Accepts overrides so filter-bar handlers can call it BEFORE the
   * corresponding setState has flushed (React batching otherwise yields
   * a one-tick-stale fetch). Falls back to the current state when no
   * override is supplied — that's what the auto-refresh / initial mount
   * effect relies on.
   */
  const load = useCallback(
    async (
      noCache: boolean,
      overrides?: { preset?: DatePresetId; type?: SearchType },
    ) => {
      setError(null);
      if (noCache) setRefreshing(true);
      else setLoading(true);

      const activePreset = overrides?.preset ?? datePreset;
      const activeType = overrides?.type ?? searchType;
      const days =
        DATE_PRESETS.find((p) => p.id === activePreset)?.days ?? 28;
      const { startDate, endDate } = rollingRangeDays(days);
      setRange({ startDate, endDate });

      try {
        const stRes = await fetch("/api/gsc/status");
        const st = (await stRes.json()) as GscStatus;
        setStatus(st);
        if (!st.ready) {
          setError(
            "GSC 尚未连接：请在服务器上配置 OAuth（GSC_CLIENT_ID / SECRET / REFRESH_TOKEN），" +
              "并确认 API 已启动。",
          );
          setLoading(false);
          setRefreshing(false);
          return;
        }

        /* Tables intentionally stay capped at 50 rows even for 3m windows —
         *  the dashboard only renders the top slice. Chart series is sized
         *  to match the largest preset so longer ranges don't truncate. */
        const [byDate, byQuery, byPage] = await Promise.all([
          postSearchAnalytics({
            startDate,
            endDate,
            dimensions: ["date"],
            rowLimit: 100,
            type: activeType,
            noCache,
          }),
          postSearchAnalytics({
            startDate,
            endDate,
            dimensions: ["query"],
            rowLimit: 50,
            type: activeType,
            noCache,
          }),
          postSearchAnalytics({
            startDate,
            endDate,
            dimensions: ["page"],
            rowLimit: 50,
            type: activeType,
            noCache,
          }),
        ]);

        const site =
          byDate.siteUrl ?? byQuery.siteUrl ?? byPage.siteUrl ?? st.defaultSite ?? null;
        setSiteUrl(site);

        const dateRows = [...(byDate.rows ?? [])].sort((a, b) =>
          (a.keys[0] ?? "").localeCompare(b.keys[0] ?? ""),
        );
        const chartSeries = dateRows.map((r) => ({
          label: r.keys[0] ?? "",
          clicks: r.clicks,
          impressions: r.impressions,
          ctr: r.ctr,
          position: r.position,
        }));
        setDaily(chartSeries);
        setSummary(aggregateFromDaily(dateRows));
        setTopQueries(byQuery.rows ?? []);
        setTopPages(byPage.rows ?? []);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
        setRefreshing(false);
        setLastUpdatedAt(new Date());
      }
    },
    [datePreset, searchType],
  );

  useEffect(() => {
    void load(false);
    // Initial mount + every time `load` identity changes (i.e. preset or
    // searchType state moves) — gives us a free refetch on filter clicks
    // without having to wire load() into every handler.
  }, [load]);

  /* Click-outside for the Search-type dropdown. */
  useEffect(() => {
    if (!searchTypeOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (searchTypeRef.current && !searchTypeRef.current.contains(e.target as Node)) {
        setSearchTypeOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [searchTypeOpen]);

  /* Auto-dismiss toast after a few seconds. */
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(id);
  }, [toast]);

  /* Centralised handler for "we know what this button means in real GSC,
     but the feature is intentionally deferred". Keeps every reserved CTA
     a single one-liner. */
  const notifyComingSoon = (feature: string) =>
    setToast(`${feature} — coming soon`);

  /* Re-render every 60s so the relative "X mins ago" label drifts up. */
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const subtitle = useMemo(() => {
    if (!siteUrl) return range.startDate + " — " + range.endDate;
    return `${siteUrl} · ${range.startDate} — ${range.endDate}`;
  }, [siteUrl, range]);

  const lastUpdatedLabel = useMemo(
    () => formatRelativeUpdate(lastUpdatedAt),
    /* nowTick forces recompute on the timer; intentional dep. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastUpdatedAt, nowTick],
  );

  /* Trend chart title is the joined list of active metrics — GSC's own
   *  pattern ("Clicks · Impressions") so the user always sees exactly
   *  which series are plotted. */
  const chartTitle = useMemo(() => {
    const labels = METRIC_ORDER.filter((k) => activeMetrics[k]).map(
      (k) => METRIC_META[k].label,
    );
    return labels.length === 0 ? "Select a metric" : labels.join(" · ");
  }, [activeMetrics]);

  return (
    <AppLayout showAgent={false}>
      <div className="mx-auto w-full max-w-[1180px] px-6 py-8 pb-16">
        {/* ── Page header ───────────────────────────────────────────── */}
        <header className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-[24px] font-semibold leading-tight tracking-tight text-gray-900">
              Performance
            </h1>
            {siteUrl && (
              <p className="mt-1 text-[12px] text-gray-500">{subtitle}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void load(true)}
              disabled={loading || refreshing}
              aria-label="刷新"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
            </button>
            <button
              type="button"
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[12.5px] font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
            >
              <ArrowDownToLine size={13} className="text-gray-500" />
              Export
            </button>
          </div>
        </header>

        {/* ── Filter Control Bar ────────────────────────────────────── */}
        <div className="mb-5 flex flex-wrap items-center gap-3">
          {/* Segmented date control — clicks immediately refetch via load()
           *  override so the highlight + dataset move in lock-step. */}
          <div className="inline-flex h-8 items-stretch overflow-hidden rounded-lg border border-gray-200 bg-white text-[12px] font-medium">
            {DATE_PRESETS.map((p, i) => {
              const active = datePreset === p.id;
              const isLast = i === DATE_PRESETS.length - 1;
              const isDetails = p.id === "details";
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    if (isDetails) {
                      notifyComingSoon("Custom range");
                      return;
                    }
                    setDatePreset(p.id);
                    void load(false, { preset: p.id });
                  }}
                  disabled={loading || refreshing}
                  className={
                    "inline-flex items-center gap-1 px-3 transition-colors " +
                    (active
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-700 hover:bg-gray-50") +
                    " disabled:cursor-not-allowed disabled:opacity-50" +
                    (!isLast ? " border-r border-gray-200" : "")
                  }
                >
                  {active && (
                    <Check size={11} strokeWidth={3} className="text-blue-600" />
                  )}
                  <span>{p.label}</span>
                </button>
              );
            })}
          </div>

          {/* Search type dropdown — real switch, passes `type` through to
           *  searchanalytics.query so the underlying dataset changes. */}
          <div ref={searchTypeRef} className="relative">
            <button
              type="button"
              onClick={() => setSearchTypeOpen((v) => !v)}
              disabled={loading || refreshing}
              aria-haspopup="listbox"
              aria-expanded={searchTypeOpen}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[12px] font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span>
                Search type:{" "}
                {SEARCH_TYPES.find((t) => t.id === searchType)?.label ?? "Web"}
              </span>
              <ChevronDown
                size={13}
                className={
                  "text-gray-400 transition-transform " +
                  (searchTypeOpen ? "rotate-180" : "")
                }
              />
            </button>
            {searchTypeOpen && (
              <ul
                role="listbox"
                className="absolute left-0 top-full z-30 mt-1 min-w-[160px] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 text-[12px] shadow-lg"
              >
                {SEARCH_TYPES.map((t) => {
                  const active = t.id === searchType;
                  return (
                    <li key={t.id} role="option" aria-selected={active}>
                      <button
                        type="button"
                        onClick={() => {
                          setSearchTypeOpen(false);
                          if (active) return;
                          setSearchType(t.id);
                          void load(false, { type: t.id });
                        }}
                        className={
                          "flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left transition-colors " +
                          (active
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-700 hover:bg-gray-50")
                        }
                      >
                        <span>{t.label}</span>
                        {active && (
                          <Check size={12} strokeWidth={3} className="text-blue-600" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Add filter — GSC's "+ New" filter builder is a non-trivial
           *  multi-step modal. Reserved until the dimension-filter UI lands;
           *  toast keeps the affordance discoverable. */}
          <button
            type="button"
            onClick={() => notifyComingSoon("Filter builder")}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[12px] font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <Plus size={13} className="text-gray-500" />
            <span>New</span>
          </button>

          {/* Right cluster */}
          <div className="ml-auto flex items-center gap-3">
            <button
              type="button"
              onClick={() => notifyComingSoon("Advanced filters")}
              aria-label="Advanced filters"
              title="Advanced filters"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              <SlidersHorizontal size={14} />
            </button>
            <span className="text-[11.5px] text-gray-400">
              Last updated: {lastUpdatedLabel}
            </span>
          </div>
        </div>

        {/* ── Error banner ──────────────────────────────────────────── */}
        {error && (
          <GscErrorBanner
            error={error}
            kind={classifyGscError(error, status)}
            redirectUri={status?.redirectUri ?? null}
            onRetry={() => void load(true)}
            retrying={refreshing}
          />
        )}

        {/* ── Row 1: GSC-style colored scorecards ─────────────────────
         * Click any card → toggles that metric's active/inactive state
         * AND retargets the trend chart below in lock-step. Default
         * selection (Clicks + Impressions) mirrors GSC's own. */}
        <section className="mb-5 grid grid-cols-2 gap-1 lg:grid-cols-4">
          <ColoredScorecard
            metricKey="clicks"
            label="Total clicks"
            value={summary ? nf(summary.clicks) : "—"}
            active={activeMetrics.clicks}
            onClick={() => toggleMetric("clicks")}
          />
          <ColoredScorecard
            metricKey="impressions"
            label="Total impressions"
            value={summary ? nf(summary.impressions) : "—"}
            active={activeMetrics.impressions}
            onClick={() => toggleMetric("impressions")}
          />
          <ColoredScorecard
            metricKey="ctr"
            label="Average CTR"
            value={summary ? `${(summary.ctr * 100).toFixed(1)}%` : "—"}
            active={activeMetrics.ctr}
            onClick={() => toggleMetric("ctr")}
          />
          <ColoredScorecard
            metricKey="position"
            label="Average position"
            value={summary ? summary.position.toFixed(1) : "—"}
            active={activeMetrics.position}
            onClick={() => toggleMetric("position")}
          />
        </section>

        {/* ── Row 2: Performance trend chart ───────────────────────
         *
         *  Title + lines + colours + tooltip rows all read off
         *  activeMetrics. Clicking a scorecard above instantly
         *  re-paints this entire section — no separate fetch needed,
         *  the daily dataset already carries all four metric columns. */}
        <section className="mb-5 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-[15px] font-semibold text-gray-900">
                {chartTitle}
              </h2>
              <p className="mt-0.5 text-xs text-gray-400">
                Trend over the selected window
              </p>
            </div>
            {/* Daily aggregation is the only granularity currently fetched
             *  (dimensions=["date"]). Weekly / Monthly roll-ups are a
             *  client-side reduction we haven't shipped yet. */}
            <button
              type="button"
              onClick={() => notifyComingSoon("Weekly / Monthly rollup")}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[12px] font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              <span>Daily</span>
              <ChevronDown size={13} className="text-gray-400" />
            </button>
          </div>
          <PerformanceTrendChart
            data={daily}
            loading={loading}
            activeMetrics={activeMetrics}
          />
        </section>

        {/* ── Row 3: Top Queries / Top Pages ───────────────────────── */}
        <section className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
          <TopQueriesCard rows={topQueries} loading={loading} />
          <TopPagesCard rows={topPages} loading={loading} />
        </section>

        {/* ── Row 4: Search Appearance / AIO Queries ───────────────── */}
        <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <SearchAppearanceCard />
          <AIOQueriesCard />
        </section>

        {/* ── Toast — reserved-feature feedback ─────────────────────
         *  Lives at the page root so it floats above every section.
         *  Self-dismisses after ~2.4s (see toast useEffect). */}
        {toast && (
          <div
            role="status"
            aria-live="polite"
            className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-gray-900/95 px-4 py-2 text-[12.5px] font-medium text-white shadow-lg ring-1 ring-black/10 backdrop-blur"
          >
            {toast}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

/* ─── Performance trend chart (zero-dependency SVG, Tremor-shaped API)
 *
 * Why hand-rolled and not Recharts/Tremor?
 *   - Strict project red-line: no new runtime deps, no tailwind.config
 *     changes for visual work.
 *   - We only need ONE chart shape (multi-line, up to 4 metrics).
 *   - Real-pixel SVG via ResizeObserver gives crisper labels than the
 *     usual viewBox-stretch tricks at the ~540 px column width used in
 *     the dashboard.
 *
 * Tremor parity:
 *   - The component effectively accepts a Tremor-like (categories,
 *     colors) contract — `activeMetrics` selects which keys in the
 *     `data` rows become "categories", and METRIC_META[key].color
 *     supplies the matching colour. Swapping in a real Tremor/Recharts
 *     implementation later is a contained edit, not a refactor.
 *
 * Multi-axis strategy:
 *   - Clicks (1e3), Impressions (1e5), CTR (0..1), Position (1..~50)
 *     all live on incompatible scales, so we normalise EACH active
 *     metric to its own 0..1 range over the visible window. The two
 *     visible axes (left + right) belong to the first two active
 *     metrics; remaining metrics still draw their lines but rely on
 *     the tooltip / legend for absolute values.
 *   - Position is "smaller is better" — `inverted: true` in
 *     METRIC_META flips its Y mapping so improvement reads as the
 *     line going UP, matching GSC's own UX.
 */
function PerformanceTrendChart({
  data,
  loading,
  activeMetrics,
}: {
  data: {
    label: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }[];
  loading: boolean;
  activeMetrics: Record<MetricKey, boolean>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  /* Measure container width via ResizeObserver so the SVG renders at
   * real device pixels — labels and stroke widths stay crisp through
   * sidebar collapses, window resizes, etc. */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const apply = (w: number) => {
      if (w > 0 && Math.abs(w - width) > 1) setWidth(Math.floor(w));
    };
    apply(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => apply(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
    // We intentionally don't re-subscribe on `width` change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const H = 300;
  const PAD = { top: 16, right: 56, bottom: 28, left: 56 };

  /* Stable list of currently plotted metrics, in canonical order. */
  const activeKeys = METRIC_ORDER.filter((k) => activeMetrics[k]);

  if (loading) {
    return (
      <div
        ref={containerRef}
        className="flex h-[300px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/40"
      >
        <RefreshCw size={18} className="animate-spin text-gray-300" />
      </div>
    );
  }
  if (!data.length) {
    return (
      <div
        ref={containerRef}
        className="flex h-[300px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/40"
      >
        <p className="text-sm text-gray-400">No data in this window.</p>
      </div>
    );
  }
  if (activeKeys.length === 0) {
    return (
      <div
        ref={containerRef}
        className="flex h-[300px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/40"
      >
        <p className="text-sm text-gray-400">
          Select at least one metric above to plot the trend.
        </p>
      </div>
    );
  }

  const plotW = Math.max(width - PAD.left - PAD.right, 1);
  const plotH = H - PAD.top - PAD.bottom;

  /* Per-metric min/max — used both for normalisation and for the visible
   *  Y-axis tick labels. Each metric is normalised to its OWN range, so
   *  a 4-line chart still shows distinct curves even when one metric is
   *  three orders of magnitude smaller than another. */
  const metricRange: Record<MetricKey, { min: number; max: number }> = {
    clicks: { min: 0, max: Math.max(...data.map((d) => d.clicks), 1) },
    impressions: { min: 0, max: Math.max(...data.map((d) => d.impressions), 1) },
    ctr: { min: 0, max: Math.max(...data.map((d) => d.ctr), 0.001) },
    position: { min: 1, max: Math.max(...data.map((d) => d.position), 1) },
  };

  const xOf = (i: number) =>
    data.length === 1
      ? PAD.left + plotW / 2
      : PAD.left + (i / (data.length - 1)) * plotW;

  /* Map a metric value to a Y coordinate using that metric's own range.
   *  Inverted metrics (only Position) reverse the mapping so improvement
   *  visually trends UP. */
  const yOf = (key: MetricKey, v: number) => {
    const { min, max } = metricRange[key];
    const span = Math.max(max - min, 1e-9);
    const norm = (v - min) / span;
    const adjusted = METRIC_META[key].inverted ? 1 - norm : norm;
    return PAD.top + plotH * (1 - adjusted);
  };

  /* Y-axis grid + labels: at most TWO axis label tracks (left + right)
   *  belonging to the first two active metrics. Other metrics still
   *  draw lines but defer numeric readouts to the tooltip. */
  const leftAxisKey: MetricKey = activeKeys[0];
  const rightAxisKey: MetricKey | null =
    activeKeys.length > 1 ? activeKeys[1] : null;

  const formatShortAxis = (key: MetricKey, t: number): string => {
    const { min, max } = metricRange[key];
    const meta = METRIC_META[key];
    /* For inverted metrics the top of the chart shows `min`, bottom
     *  shows `max`; for normal metrics the opposite. `t` is 0..1 along
     *  the chart height, bottom = 0. */
    const raw = meta.inverted ? max - t * (max - min) : min + t * (max - min);
    if (key === "ctr") return `${(raw * 100).toFixed(1)}%`;
    if (key === "position") return raw.toFixed(1);
    if (raw >= 1_000_000) return (raw / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    if (raw >= 1_000) return (raw / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
    return String(Math.round(raw));
  };

  const ticks = [0, 0.25, 0.5, 0.75, 1];

  /* X-axis labels — at most 5, evenly spread, always including endpoints
   *  so the window boundaries are unambiguous. */
  const xLabelIdxs: number[] = (() => {
    if (data.length <= 6) return data.map((_, i) => i);
    const n = data.length - 1;
    return [0, Math.round(n * 0.25), Math.round(n * 0.5), Math.round(n * 0.75), n];
  })();

  /* "MM-DD" — strip the year prefix from "YYYY-MM-DD" since the year is
   *  implied by the page subtitle. */
  const shortDate = (s: string) => (s.length >= 10 ? s.slice(5) : s);

  /* Build SVG `d` strings for every active metric. */
  const linePaths = activeKeys.map((key) => ({
    key,
    d: data
      .map(
        (row, i) =>
          `${i === 0 ? "M" : "L"} ${xOf(i).toFixed(2)} ${yOf(key, row[key]).toFixed(2)}`,
      )
      .join(" "),
  }));

  /* Tooltip horizontal offset: clamp inside the chart so it never gets
   *  clipped by the card's rounded corners. */
  const tooltipLeftPct =
    hoverIdx === null
      ? 0
      : Math.min(Math.max((xOf(hoverIdx) / width) * 100, 6), 94);

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        width={width}
        height={H}
        className="block"
        onMouseLeave={() => setHoverIdx(null)}
      >
        {/* Horizontal grid + (up to two) Y-axis tick label columns.
         *  Tick colours subtly tint with the corresponding metric so a
         *  glance immediately tells you which axis maps to which line. */}
        {ticks.map((t) => {
          const y = PAD.top + plotH * (1 - t);
          return (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y}
                y2={y}
                stroke="#f1f3f5"
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8}
                y={y + 3}
                textAnchor="end"
                fontSize={10}
                fill={METRIC_META[leftAxisKey].color}
                opacity={0.75}
              >
                {formatShortAxis(leftAxisKey, t)}
              </text>
              {rightAxisKey && (
                <text
                  x={width - PAD.right + 8}
                  y={y + 3}
                  textAnchor="start"
                  fontSize={10}
                  fill={METRIC_META[rightAxisKey].color}
                  opacity={0.75}
                >
                  {formatShortAxis(rightAxisKey, t)}
                </text>
              )}
            </g>
          );
        })}

        {/* Metric lines — drawn in canonical order so the visual stacking
         *  is stable as the user toggles cards on/off. */}
        {linePaths.map(({ key, d }) => (
          <path
            key={key}
            d={d}
            fill="none"
            stroke={METRIC_META[key].color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {/* X-axis labels */}
        {xLabelIdxs.map((i) => (
          <text
            key={i}
            x={xOf(i)}
            y={H - 8}
            textAnchor="middle"
            fontSize={10}
            fill="#9ca3af"
          >
            {shortDate(data[i].label)}
          </text>
        ))}

        {/* Invisible hover targets — each segment owns the midpoint band
         *  between its neighbours, so the indicator snaps to the nearest
         *  data point as the cursor moves. */}
        {data.map((_, i) => {
          const x = xOf(i);
          const prev = i === 0 ? PAD.left : (xOf(i - 1) + x) / 2;
          const next =
            i === data.length - 1 ? width - PAD.right : (x + xOf(i + 1)) / 2;
          return (
            <rect
              key={i}
              x={prev}
              y={PAD.top}
              width={Math.max(next - prev, 1)}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHoverIdx(i)}
            />
          );
        })}

        {/* Hover indicator — dashed guide + one dot per active metric. */}
        {hoverIdx !== null && (
          <g>
            <line
              x1={xOf(hoverIdx)}
              x2={xOf(hoverIdx)}
              y1={PAD.top}
              y2={PAD.top + plotH}
              stroke="#cbd5e1"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            {activeKeys.map((key) => (
              <circle
                key={key}
                cx={xOf(hoverIdx)}
                cy={yOf(key, data[hoverIdx][key])}
                r={4}
                fill="#fff"
                stroke={METRIC_META[key].color}
                strokeWidth={2}
              />
            ))}
          </g>
        )}
      </svg>

      {/* Tooltip card — one row per active metric, formatted via
       *  METRIC_META.format so the units (raw counts vs %, decimals)
       *  always match the scorecards above. */}
      {hoverIdx !== null && (
        <div
          className="pointer-events-none absolute top-2 z-10 -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg"
          style={{ left: `${tooltipLeftPct}%` }}
        >
          <div className="font-semibold text-gray-800">
            {data[hoverIdx].label}
          </div>
          {activeKeys.map((key) => {
            const meta = METRIC_META[key];
            return (
              <div key={key} className="mt-1 flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: meta.color }}
                />
                <span className="text-gray-500">{meta.label}:</span>
                <span className="font-medium tabular-nums text-gray-800">
                  {meta.format(data[hoverIdx][key])}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Legend — also driven by activeKeys so it always matches what's
       *  actually plotted. */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-1.5 text-[11px] text-gray-500">
        {activeKeys.map((key) => (
          <div key={key} className="flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: METRIC_META[key].color }}
            />
            {METRIC_META[key].label}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── GSC-style colored scorecard ─────────────────────────────────── */
/**
 * Two-state metric tile. Click toggles the metric on/off; the chart
 * below reads the same `activeMetrics` state, so the two are
 * permanently in lock-step.
 *
 *   - ACTIVE   → saturated fill, white text, filled CheckSquare.
 *   - INACTIVE → white fill, accent-colour border + text, empty Square.
 *
 * Tailwind can't safelist arbitrary hex via dynamic classes, so we
 * resolve the accent through inline `style`. That keeps METRIC_META as
 * the single source of truth for colour while still respecting the JIT.
 *
 * The DOM shape (a single rounded panel containing label row → big
 * value → corner HelpCircle) is preserved verbatim from the previous
 * static card so the surrounding grid layout is unchanged — only
 * interactivity and skin variables were added.
 */
function ColoredScorecard({
  metricKey,
  label,
  value,
  active,
  onClick,
}: {
  metricKey: MetricKey;
  label: string;
  value: string;
  active: boolean;
  onClick: () => void;
}) {
  const accent = METRIC_META[metricKey].color;
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={active}
      aria-label={`${label} — ${active ? "active" : "inactive"}, click to toggle`}
      onClick={onClick}
      onKeyDown={onKeyDown}
      style={
        active
          ? { backgroundColor: accent, borderColor: accent, color: "#fff" }
          : { backgroundColor: "#fff", borderColor: accent, color: accent }
      }
      className="relative cursor-pointer overflow-hidden rounded-lg border-2 p-4 transition-all hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
    >
      <div className="flex items-center gap-1.5 text-[12px] font-medium tracking-tight">
        {active ? (
          <CheckSquare size={13} strokeWidth={2.4} className="opacity-95" />
        ) : (
          <Square size={13} strokeWidth={2.4} className="opacity-70" />
        )}
        <span>{label}</span>
      </div>
      <p className="mt-3 text-[28px] font-semibold leading-none tabular-nums">
        {value}
      </p>
      <HelpCircle
        size={14}
        className="absolute bottom-2.5 right-2.5"
        style={{ opacity: active ? 0.4 : 0.55 }}
        strokeWidth={2}
      />
    </div>
  );
}

/* ─── Sort icon (shared between tables) ──────────────────────────── */

function SortIcon() {
  return (
    <ChevronsUpDown
      size={11}
      className="ml-1 inline-block -translate-y-px text-gray-400"
      strokeWidth={2}
    />
  );
}

/* ─── Filter pills ────────────────────────────────────────────────── */

function FilterPill({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "rounded-full bg-indigo-600 px-3 py-1 text-[11px] font-medium text-white shadow-sm transition-colors hover:bg-indigo-700"
          : "rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
      }
    >
      {label}
    </button>
  );
}

/* ─── Search input ────────────────────────────────────────────────── */

function SearchInput({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="relative w-[200px]">
      <Search
        size={13}
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-7 w-full rounded-md border border-gray-200 bg-gray-50/70 pl-7 pr-2 text-[12px] text-gray-700 placeholder:text-gray-400 focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gray-100"
      />
    </div>
  );
}

/* ─── Top Queries card ───────────────────────────────────────────── */

type QueryFilter = "all" | "striking" | "lowCtr";

function TopQueriesCard({ rows, loading }: { rows: GscRow[]; loading: boolean }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<QueryFilter>("all");

  const filtered = useMemo(() => {
    let list = rows;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((r) => (r.keys[0] ?? "").toLowerCase().includes(q));
    }
    if (filter === "striking") {
      list = list.filter((r) => r.position >= 8 && r.position <= 20);
    } else if (filter === "lowCtr") {
      list = list.filter((r) => r.ctr < 0.02);
    }
    // No hard truncation — the table's wrapper scrolls vertically and the
    // sticky <thead> keeps column labels in view while the user scans.
    return list;
  }, [rows, search, filter]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-5 pt-4">
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-gray-900">Top Queries</h2>
          {rows.length > 0 && (
            <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10.5px] font-medium tabular-nums text-gray-500">
              {filtered.length}
              {filtered.length !== rows.length ? ` / ${rows.length}` : ""}
            </span>
          )}
        </div>
        <SearchInput
          placeholder="Search keywords..."
          value={search}
          onChange={setSearch}
        />
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap items-center gap-2 px-5 pt-3 pb-3">
        <FilterPill active={filter === "all"} label="All" onClick={() => setFilter("all")} />
        <FilterPill
          active={filter === "striking"}
          label="Striking Distance"
          onClick={() => setFilter("striking")}
        />
        <FilterPill
          active={filter === "lowCtr"}
          label="Low CTR"
          onClick={() => setFilter("lowCtr")}
        />
      </div>

      {/* Table — fixed-height scroll region with a sticky header so the
       *  column labels never disappear while users scan ~50 rows.
       *  max-h tuned so ~10 rows are visible (≈ industry SaaS default). */}
      <div className="max-h-[460px] overflow-y-auto">
        <table className="w-full text-[13px]">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-y border-gray-100 bg-gray-50 text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
              <th className="px-5 py-2.5 text-left">QUERY</th>
              <th className="px-3 py-2.5 text-right">
                <span className="flex items-center justify-end gap-1">
                  CLICKS
                  <SortIcon />
                </span>
              </th>
              <th className="px-3 py-2.5 text-right">
                <span className="flex items-center justify-end gap-1">
                  IMPRESSIONS
                  <SortIcon />
                </span>
              </th>
              <th className="px-3 py-2.5 text-right">
                <span className="flex items-center justify-end gap-1">
                  POSITION
                  <SortIcon />
                </span>
              </th>
              <th className="px-5 py-2.5 text-right">ACTION</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <SkeletonRows cols={5} />
            ) : filtered.length === 0 ? (
              <EmptyRow colSpan={5} text="No queries match the current filter." />
            ) : (
              filtered.map((r, idx) => (
                <tr
                  key={idx}
                  className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50/60"
                >
                  <td className="px-5 py-3 text-left align-middle text-gray-800">
                    <span className="line-clamp-2" title={r.keys[0]}>
                      {r.keys[0] ?? "—"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-gray-700">
                    {nf(r.clicks)}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-gray-700">
                    {nf(r.impressions)}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-gray-700">
                    {r.position.toFixed(1)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {/* AI Expand → hand the high-potential query off to
                     *  AI Creator in CREATE mode. The receiving page
                     *  reads `keyword` from search params and pre-pins
                     *  it as a selected pill on mount. */}
                    <GhostActionButton
                      icon={<Sparkles size={12} />}
                      label="AI Expand"
                      tone="indigo"
                      onClick={() =>
                        navigate(
                          `/ai-creator?mode=create&keyword=${encodeURIComponent(r.keys[0] ?? "")}`,
                        )
                      }
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Top Pages card ─────────────────────────────────────────────── */

type PageFilter = "all" | "decay";

function TopPagesCard({ rows, loading }: { rows: GscRow[]; loading: boolean }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<PageFilter>("all");

  const filtered = useMemo(() => {
    let list = rows;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((r) => (r.keys[0] ?? "").toLowerCase().includes(q));
    }
    if (filter === "decay") {
      list = list.filter((r) => r.ctr < 0.025);
    }
    return list;
  }, [rows, search, filter]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 px-5 pt-4">
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-gray-900">Top Pages</h2>
          {rows.length > 0 && (
            <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10.5px] font-medium tabular-nums text-gray-500">
              {filtered.length}
              {filtered.length !== rows.length ? ` / ${rows.length}` : ""}
            </span>
          )}
        </div>
        <SearchInput
          placeholder="Search page path..."
          value={search}
          onChange={setSearch}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 px-5 pt-3 pb-3">
        <FilterPill active={filter === "all"} label="All" onClick={() => setFilter("all")} />
        <FilterPill
          active={filter === "decay"}
          label="Content Decay"
          onClick={() => setFilter("decay")}
        />
      </div>

      <div className="max-h-[460px] overflow-y-auto">
        <table className="w-full text-[13px]">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-y border-gray-100 bg-gray-50 text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
              <th className="px-5 py-2.5 text-left">PAGE PATH</th>
              <th className="px-3 py-2.5 text-right">
                <span className="flex items-center justify-end gap-1">
                  IMPRESSIONS
                  <SortIcon />
                </span>
              </th>
              <th className="px-3 py-2.5 text-right">
                <span className="flex items-center justify-end gap-1">
                  CTR
                  <SortIcon />
                </span>
              </th>
              <th className="px-5 py-2.5 text-right">ACTION</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <SkeletonRows cols={4} />
            ) : filtered.length === 0 ? (
              <EmptyRow colSpan={4} text="No pages match the current filter." />
            ) : (
              filtered.map((r, idx) => (
                <tr
                  key={idx}
                  className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50/60"
                >
                  <td className="px-5 py-3 text-left align-middle text-gray-800">
                    <span
                      className="block truncate"
                      title={r.keys[0]}
                      style={{ maxWidth: 220 }}
                    >
                      {toRelativePath(r.keys[0])}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-gray-700">
                    {nf(r.impressions)}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-gray-700">
                    {(r.ctr * 100).toFixed(1)}%
                  </td>
                  <td className="px-5 py-3 text-right">
                    {/* Optimize → ship the existing page path to AI
                     *  Creator in OPTIMIZE mode. We send the RELATIVE
                     *  path (toRelativePath) so the URL bar stays
                     *  short and the optimize panel can resolve it
                     *  against the brand's canonical origin if needed. */}
                    <GhostActionButton
                      icon={<Edit3 size={12} />}
                      label="Optimize"
                      tone="gray"
                      onClick={() =>
                        navigate(
                          `/ai-creator?mode=optimize&url=${encodeURIComponent(toRelativePath(r.keys[0]))}`,
                        )
                      }
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Strip protocol + host so a row shows only the path segment. */
function toRelativePath(url: string | undefined): string {
  if (!url) return "—";
  try {
    const u = new URL(url);
    return u.pathname + (u.search || "");
  } catch {
    return url;
  }
}

/* ─── Search Appearance (BarList-equivalent) ─────────────────────── */
/*
 * NOTE — placeholder dataset.
 * GSC's search-appearance breakdown lives in a separate query path (set
 * `searchAppearance` as a dimension in searchanalytics.query, with an
 * empty searchAppearance filter group). The backend route doesn't expose
 * that yet, so this card renders a fixed sample shape until the real
 * fetch is wired in. The "Sample data" pill below makes that explicit.
 */
const SEARCH_APPEARANCE_DATA = [
  { name: "Normal Web Results", value: 42100 },
  { name: "Videos", value: 12400 },
  { name: "FAQ Rich Results", value: 8900 },
  { name: "Review Snippets", value: 5600 },
];

function SearchAppearanceCard() {
  const max = Math.max(...SEARCH_APPEARANCE_DATA.map((d) => d.value));

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <h2 className="text-[15px] font-semibold text-gray-900">Search Appearance</h2>
        <span
          className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700"
          title="Backend integration for the searchAppearance dimension hasn't shipped yet — the values below are illustrative."
        >
          Sample data
        </span>
      </div>
      <p className="mt-0.5 text-xs text-gray-400">Clicks Distribution (by Type)</p>

      <div className="mt-5 space-y-3.5">
        {SEARCH_APPEARANCE_DATA.map((d) => {
          const pct = (d.value / max) * 100;
          return (
            <div key={d.name}>
              <div className="flex items-baseline justify-between text-[12.5px]">
                <span className="text-gray-700">{d.name}</span>
                <span className="tabular-nums font-medium text-gray-900">{nf(d.value)}</span>
              </div>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full bg-indigo-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── AIO / Conversational Queries ───────────────────────────────── */
/*
 * NOTE — placeholder dataset.
 * GSC has no native "AI Overview" dimension; isolating conversational /
 * AIO traffic requires either:
 *   (a) heuristically classifying queries from the regular dataset, or
 *   (b) plugging in a third-party AIO tracker (e.g. SerpAPI / Serpstat).
 * Both are out of scope for the current iteration — this card stays on
 * a fixed sample until that classifier ships. The "Sample data" pill
 * makes the mock status visible to the user.
 */
const AIO_QUERIES = [
  { query: "what is a seo dashboard?", clicks: 842, position: 2.1 },
  { query: "how to optimize ctr?", clicks: 654, position: 3.4 },
  { query: "how to research aio keywords?", clicks: 432, position: 1.8 },
];

function AIOQueriesCard() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <h2 className="text-[15px] font-semibold text-gray-900">AIO / Conversational Queries</h2>
        <Bot size={15} className="text-gray-400" />
        <span
          className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700"
          title="No native GSC dimension for AI Overview — needs a classifier or external tracker. Showing illustrative data for now."
        >
          Sample data
        </span>
      </div>
      <p className="mt-0.5 text-xs text-gray-400">AI-driven search performance</p>

      <div className="mt-4 overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-gray-100 text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
              <th className="py-2 pr-3 text-left">QUERY</th>
              <th className="px-2 py-2 text-right">CLICKS</th>
              <th className="px-2 py-2 text-right">POSITION</th>
              <th className="py-2 pl-3 text-right">ACTION</th>
            </tr>
          </thead>
          <tbody>
            {AIO_QUERIES.map((q, idx) => (
              <tr
                key={idx}
                className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50/60"
              >
                <td className="py-3 pr-3 italic text-gray-800">
                  &ldquo;{q.query}&rdquo;
                </td>
                <td className="px-2 py-3 text-right tabular-nums text-gray-700">
                  {nf(q.clicks)}
                </td>
                <td className="px-2 py-3 text-right tabular-nums text-gray-700">
                  {q.position.toFixed(1)}
                </td>
                <td className="py-3 pl-3 text-right">
                  <GhostActionButton
                    icon={<Zap size={12} />}
                    label="AIO Rewrite"
                    tone="indigo"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Shared row primitives ──────────────────────────────────────── */

function GhostActionButton({
  icon,
  label,
  tone,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  tone: "indigo" | "gray";
  onClick?: () => void;
}) {
  const palette =
    tone === "indigo"
      ? "text-indigo-600 hover:bg-indigo-50"
      : "text-gray-700 hover:bg-gray-100 border border-gray-200";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11.5px] font-medium transition-colors ${palette}`}
    >
      <span className="grid place-items-center">{icon}</span>
      {label}
    </button>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <tr key={i} className="border-b border-gray-50 last:border-b-0">
          {Array.from({ length: cols }).map((_, j) => (
            <td key={j} className="px-5 py-3">
              <div className="h-3 w-full max-w-[120px] animate-pulse rounded bg-gray-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function EmptyRow({ colSpan, text }: { colSpan: number; text: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-5 py-8 text-center text-[12.5px] text-gray-400">
        {text}
      </td>
    </tr>
  );
}

/* ─── GSC error banner — actionable per-category UX ────────────────── */
/**
 * Different GSC failure modes need different CTAs:
 *
 *   needs-reauth → Google revoked / expired the refresh token. The fix is
 *                  a quick one-shot re-auth via /api/gsc/oauth/start; the
 *                  callback now auto-writes the new token to .env and
 *                  applies it live, so the user just clicks Reconnect,
 *                  finishes the Google consent flow, and presses Retry.
 *
 *   needs-config → Backend env vars not set; show the manual instructions
 *                  pointing at apps/api/.env.
 *
 *   transient   → Network blip / Google outage; just offer Retry.
 *
 *   other       → Show raw error verbatim + Retry, so the operator can
 *                 still file a useful bug report.
 */
function GscErrorBanner({
  error,
  kind,
  redirectUri,
  onRetry,
  retrying,
}: {
  error: string;
  kind: GscErrorKind;
  redirectUri: string | null;
  onRetry: () => void;
  retrying: boolean;
}) {
  const reconnect = () => {
    /* Open the OAuth start endpoint in a new window so the user can
       finish Google consent without losing the GSC overview state. */
    window.open("/api/gsc/oauth/start", "_blank", "noopener,noreferrer");
  };

  /* The reconnect UX has two flavours, decided by GSC_REDIRECT_URI in
     apps/api/.env:
       - Local callback ("/api/gsc/oauth/callback"): Google sends the
         code straight back to our API which auto-writes the new
         refresh_token to .env and applies it live. Truly one-click.
       - OAuth Playground: Google redirects to Google's own debugger UI;
         the user has to copy the new token themselves and paste it into
         apps/api/.env. We surface explicit step-by-step instructions
         so this manual hop isn't a mystery. */
  const usesLocalCallback =
    !!redirectUri && /\/api\/gsc\/oauth\/callback(\?|$)/i.test(redirectUri);
  const usesPlayground =
    !!redirectUri && redirectUri.toLowerCase().includes("oauthplayground");

  const palette =
    kind === "transient"
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : kind === "needs-config"
      ? "border-rose-200 bg-rose-50 text-rose-900"
      : "border-indigo-200 bg-indigo-50 text-indigo-900";

  const headline =
    kind === "needs-reauth"
      ? "Google Search Console 登录已失效"
      : kind === "needs-config"
      ? "Google Search Console 尚未配置"
      : kind === "transient"
      ? "暂时无法加载数据"
      : "无法加载数据";

  const body =
    kind === "needs-reauth" ? (
      <>
        <p className="mt-1 text-sm/relaxed">
          Refresh token 已过期或被撤销（Google 返回
          <code className="mx-1 rounded bg-white/60 px-1.5 py-0.5 text-[12px] font-mono">
            {error}
          </code>
          ）。
        </p>
        {usesLocalCallback ? (
          <p className="mt-2 text-xs text-current/80">
            点击下方按钮 → 完成 Google 授权 → 关闭 OAuth 标签 → 回到这里点{" "}
            <strong>重试</strong>。 服务器会自动把新 token 写入{" "}
            <code className="rounded bg-white/60 px-1">apps/api/.env</code>{" "}
            并立即应用，无需重启。
          </p>
        ) : usesPlayground ? (
          <details className="mt-2 text-xs text-current/80" open>
            <summary className="cursor-pointer select-none font-semibold">
              使用 OAuth Playground 获取新 Token（手动步骤）
            </summary>
            <ol className="mt-2 ml-5 list-decimal space-y-1.5 leading-relaxed">
              <li>点击下方 <strong>重新连接 Google</strong>（在新标签打开 Google 授权）。</li>
              <li>登录拥有 GSC 权限的 Google 账号 → 同意授权。</li>
              <li>
                跳到 <code className="rounded bg-white/60 px-1">developers.google.com/oauthplayground</code>{" "}
                后，左侧 Step 2 → <strong>Exchange authorization code for tokens</strong> → 复制
                <strong> refresh_token</strong>。
              </li>
              <li>
                打开 <code className="rounded bg-white/60 px-1">apps/api/.env</code>，把
                <code className="rounded bg-white/60 px-1">GSC_REFRESH_TOKEN=...</code> 这一行改成新 token。
              </li>
              <li>保存（API 在 <code className="rounded bg-white/60 px-1">tsx watch</code> 模式下会自动重启），回到这里点 <strong>重试</strong>。</li>
            </ol>
            <p className="mt-2 text-[11px] text-current/70">
              如果想以后免去这个手动步骤，可以把 <code className="rounded bg-white/60 px-1">.env</code> 中的{" "}
              <code className="rounded bg-white/60 px-1">GSC_REDIRECT_URI</code> 改为{" "}
              <code className="rounded bg-white/60 px-1">http://localhost:8787/api/gsc/oauth/callback</code>{" "}
              并到 Google Cloud Console 把同一个 URI 加进 OAuth client 的允许列表，之后授权完会自动写 .env。
            </p>
          </details>
        ) : (
          <p className="mt-2 text-xs text-current/80">
            点击下方按钮在 Google 重新授权一次，按提示更新{" "}
            <code className="rounded bg-white/60 px-1">apps/api/.env</code> 中的{" "}
            <code className="rounded bg-white/60 px-1">GSC_REFRESH_TOKEN</code>，再回来点 <strong>重试</strong>。
          </p>
        )}
      </>
    ) : kind === "needs-config" ? (
      <>
        <p className="mt-1 text-sm whitespace-pre-wrap">{error}</p>
        <p className="mt-2 text-xs text-current/70">
          在 <code className="rounded bg-white/60 px-1">apps/api/.env</code> 中填写
          <code className="mx-1 rounded bg-white/60 px-1">GSC_CLIENT_ID</code>
          /
          <code className="mx-1 rounded bg-white/60 px-1">GSC_CLIENT_SECRET</code>
          /
          <code className="mx-1 rounded bg-white/60 px-1">GSC_REDIRECT_URI</code>
          后重启 API，再回到这里点&nbsp;<strong>重试</strong>。
        </p>
      </>
    ) : (
      <p className="mt-1 text-sm whitespace-pre-wrap break-words">{error}</p>
    );

  return (
    <div className={`mb-6 flex gap-3 rounded-xl border px-4 py-3 ${palette}`}>
      <AlertCircle className="shrink-0 mt-0.5" size={18} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold">{headline}</p>
        {body}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {kind === "needs-reauth" && (
            <button
              type="button"
              onClick={reconnect}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 transition-colors"
            >
              <Plug size={12} />
              重新连接 Google
              <ExternalLink size={11} className="opacity-80" />
            </button>
          )}
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="inline-flex items-center gap-1.5 rounded-lg border border-current/20 bg-white/70 px-3 py-1.5 text-xs font-semibold hover:bg-white disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={12} className={retrying ? "animate-spin" : ""} />
            {retrying ? "重试中…" : "重试"}
          </button>
        </div>
      </div>
    </div>
  );
}
